import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import {
    useGetNextMsDeliveryDateQuery,
    useUpdateMicrostoresReassortmentForDryFlowMutation,
    useUpdateMicrostoresReassortmentForFreshFlowMutation,
    TotadminPermission,
} from 'data/__generated__';

import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { Loader, LoaderModeType } from 'components/Loader';
import { TotemDatePicker } from 'components/TotemDatePicker';

import { dateFromString, formatDateAsAnniversary } from 'helpers/dateTimes';
import { confirmPopup } from 'components/Popups/Confirm';

import { ColumnSelectionSection, type SelectableColumn } from './ColumnSelectionSection';

export type QuantityGenerationColumn = SelectableColumn;

export function QuantityGenerationPopup({
    isOpen,
    setIsOpen,
    initialColumns = [],
    siteId,
    isLocked = false,
    showOpenSitesShortcut = false,
}: {
    isOpen: boolean;
    setIsOpen: (arg: boolean) => void;
    initialColumns?: QuantityGenerationColumn[];
    siteId?: string;
    isLocked?: boolean;
    showOpenSitesShortcut?: boolean;
}) {
    const {
        data: nextMSDeliveryDateData,
        loading: nextMSDeliveryDateLoading,
        error: nextMSDeliveryDateError,
    } = useGetNextMsDeliveryDateQuery();

    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    // Seeded once at mount from the columns selected on the page (or pre-filled for a detail page).
    // Every caller mounts this popup conditionally (`{isOpen ? <Popup/> : null}`), so opening it
    // remounts the component with a fresh `initialColumns`.
    const [columns, setColumns] = useState<QuantityGenerationColumn[]>(initialColumns);

    useEffect(() => {
        if (nextMSDeliveryDateData?.getNextMSDeliveryDate) {
            setSelectedDate(dateFromString(nextMSDeliveryDateData.getNextMSDeliveryDate));
        }
    }, [nextMSDeliveryDateData]);

    const [
        updateMicrostoresReassortmentForFreshFlowMutation,
        { loading: updateMicrostoresReassortmentForFreshFlowMutationLoading },
    ] = useUpdateMicrostoresReassortmentForFreshFlowMutation();

    const [
        updateMicrostoresReassortmentForDryFlowMutation,
        { loading: updateMicrostoresReassortmentForDryFlowMutationLoading },
    ] = useUpdateMicrostoresReassortmentForDryFlowMutation();

    if (nextMSDeliveryDateLoading) {
        return (
            <TotemPopup title="Génération des quantités" isOpen={isOpen} setIsOpen={setIsOpen} contentOverflow="auto">
                <Loader mode={LoaderModeType.Spin} />
            </TotemPopup>
        );
    }

    if (nextMSDeliveryDateError) {
        throw new Error('Une erreur est survenue lors de la récupération de la prochaine date de livraison');
    }

    const columnIds = columns.map(({ _id }) => _id);

    async function updateMicrostoreReassortmentForFreshFlow() {
        if (!selectedDate) {
            toast.error('Veuillez sélectionner une date de livraison');
            return;
        }

        const hasConfirmed = await confirmPopup({
            title: 'Confirmer',
            message:
                'Vous allez remplacer tout le réassort manuel du J+1 du/des site(s) avec les prédictions, êtes-vous sûr ?',
        });
        if (!hasConfirmed) {
            return;
        }

        const { data } = await updateMicrostoresReassortmentForFreshFlowMutation({
            variables: {
                microstoreColumnIds: columnIds,
                reassortmentDate: formatDateAsAnniversary({ dateTime: selectedDate }),
            },
        });

        if (!data) {
            throw new Error('Une erreur est survenue lors de la mise à jour du/des microstore(s) avec les prédictions');
        }

        const {
            updateMicrostoresReassortmentForFreshFlowMutation: { errors, microstoreColumns, warnings },
        } = data;

        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });
        }

        if (warnings.length) {
            warnings.forEach((warning, index) => {
                toast.warning(<span key={index}>Warning : {warning}</span>, { autoClose: false });
            });
        }

        if (errors.length + warnings.length > 1) {
            toast.info('Cliquez pour fermer toutes les notifications', {
                autoClose: false,
                onClick: () => toast.dismiss(),
            });
        }

        if (microstoreColumns) {
            toast.success('Le(s) microstore(s) a/ont bien été modifié(s) !');
        }

        setIsOpen(false);
    }

    async function updateMicrostoreReassortmentForDryFlow() {
        if (!selectedDate) {
            toast.error('Veuillez sélectionner une date de livraison');
            return;
        }

        const hasConfirmed = await confirmPopup({
            title: 'Confirmer',
            message: 'Vous allez remplacer tout le réassort par stock cible du J+1 du/des site(s), êtes-vous sûr ?',
        });
        if (!hasConfirmed) {
            return;
        }

        const { data } = await updateMicrostoresReassortmentForDryFlowMutation({
            variables: {
                microstoreColumnIds: columnIds,
                reassortmentDate: formatDateAsAnniversary({ dateTime: selectedDate }),
            },
        });

        if (!data) {
            throw new Error(
                'Une erreur est survenue lors de la mise à jour du/des microstore(s) avec les stocks cibles',
            );
        }

        const {
            updateMicrostoresReassortmentForDryFlowMutation: { errors, microstoreColumns },
        } = data;

        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });

            if (errors.length > 1) {
                toast.info('Cliquez pour fermer toutes les notifications', {
                    autoClose: false,
                    onClick: () => toast.dismiss(),
                });
            }
        } else if (microstoreColumns) {
            toast.success('Le(s) microstore(s) a/ont bien été modifié(s) !');
        }

        setIsOpen(false);
    }

    const hasColumns = columns.length > 0;

    return (
        <TotemPopup title="Génération des quantités" isOpen={isOpen} setIsOpen={setIsOpen} contentOverflow="auto">
            <Content>
                <Section>
                    <SectionTitle>Date de réassort</SectionTitle>
                    <TotemDatePicker
                        selected={selectedDate}
                        onChange={(date) => setSelectedDate(date)}
                        label="Date de réassort"
                        shouldAllowPastDates={false}
                        isDisabled
                    />
                </Section>
                <ColumnSelectionSection
                    columns={columns}
                    onColumnsChange={setColumns}
                    siteId={siteId}
                    isLocked={isLocked}
                    showOpenSitesShortcut={showOpenSitesShortcut}
                    nextMSDeliveryDate={nextMSDeliveryDateData?.getNextMSDeliveryDate}
                />
                <Section>
                    <SectionTitle>Flow DRY</SectionTitle>
                    <TotemPrimaryLoadingButton
                        type="button"
                        title="Générer les quantités pour flow DRY"
                        loading={updateMicrostoresReassortmentForDryFlowMutationLoading}
                        disabled={!hasColumns}
                        onClick={updateMicrostoreReassortmentForDryFlow}
                        useIcon={false}
                        label="Générer les quantités pour flow DRY"
                        requiredPermissions={[TotadminPermission.MicrostoreColumnsManage]}
                    />
                </Section>
                <Section>
                    <SectionTitle>Flow FRESH</SectionTitle>
                    <TotemPrimaryLoadingButton
                        type="button"
                        title="Générer les quantités pour flow FRESH (prédictions + stock cible)"
                        loading={updateMicrostoresReassortmentForFreshFlowMutationLoading}
                        disabled={!hasColumns}
                        onClick={updateMicrostoreReassortmentForFreshFlow}
                        useIcon={false}
                        label="Générer les quantités pour flow FRESH (prédictions + stock cible)"
                        requiredPermissions={[TotadminPermission.MicrostoreColumnsManage]}
                    />
                </Section>
            </Content>
        </TotemPopup>
    );
}

const Content = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;

    & > :not(:first-child) {
        margin-top: 10px;
        border-top: 1px solid ${({ theme }) => theme.darkBorderColor};
    }
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 5px;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const SectionTitle = styled.div`
    text-align: center;
    font-size: 20px;
    font-weight: 800;
`;
